import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Keeps the `noindex` directive in sync with the route during SPA navigation.
 *
 * The directive itself is set by the bootstrap script in public/index.html,
 * which runs before this bundle loads. That is deliberate: Amplify rewrites any
 * URL without its own prerendered file to /index.html, the prerendered
 * HOMEPAGE, so /chat, /reports, /compatibility-report and every typo'd path
 * returned HTTP 200 carrying a byte-identical copy of the homepage, four-plus
 * duplicate URLs competing with "/" for our head terms. Deciding that in React
 * was not reliable enough: /chat renders nothing until a geo probe resolves,
 * and /compatibility-report redirects to "/" before this component ever sees
 * its own path, so both slipped through with no directive at all.
 *
 * `noindex` rather than a robots.txt `Disallow`: the URLs are already indexed,
 * and a disallow would block the crawl that has to happen for Google to read
 * the directive and drop them. `follow` keeps link equity flowing.
 *
 * Keep INDEXABLE in sync with ALLOW in public/index.html and reactSnap.include.
 */

const INDEXABLE = new Set([
  "/widgets",
  "/ketu-mahadasha",
  "/shukra-mahadasha",
  "/surya-mahadasha",
  "/chandra-mahadasha",
  "/mangal-mahadasha",
  "/rahu-mahadasha",
  "/guru-mahadasha",
  "/shani-mahadasha",
  "/budh-mahadasha",
  "/ganesh-chaturthi-2027",
  "/dussehra-2027",
  "/chhath-puja-2027",
  "/guru-purnima-2027",
  "/akshaya-tritiya-2027",
  "/ram-navami-2027",
  "/basant-panchami-2027",
  "/ashwini-nakshatra",
  "/bharani-nakshatra",
  "/krittika-nakshatra",
  "/rohini-nakshatra",
  "/mrigashira-nakshatra",
  "/ardra-nakshatra",
  "/punarvasu-nakshatra",
  "/pushya-nakshatra",
  "/ashlesha-nakshatra",
  "/magha-nakshatra",
  "/purva-phalguni-nakshatra",
  "/uttara-phalguni-nakshatra",
  "/hasta-nakshatra",
  "/chitra-nakshatra",
  "/swati-nakshatra",
  "/vishakha-nakshatra",
  "/anuradha-nakshatra",
  "/jyeshtha-nakshatra",
  "/mula-nakshatra",
  "/purva-ashadha-nakshatra",
  "/uttara-ashadha-nakshatra",
  "/shravana-nakshatra",
  "/dhanishta-nakshatra",
  "/shatabhisha-nakshatra",
  "/purva-bhadrapada-nakshatra",
  "/uttara-bhadrapada-nakshatra",
  "/revati-nakshatra",
  "/vivah-muhurat-november-2026",
  "/vivah-muhurat-december-2026",
  "/griha-pravesh-muhurat-november-2026",
  "/griha-pravesh-muhurat-december-2026",
  "/mundan-muhurat-november-2026",
  "/mundan-muhurat-december-2026",
  "/naamkaran-muhurat-november-2026",
  "/naamkaran-muhurat-december-2026",
  "/pitra-dosh",
  "/guru-chandal-dosh",
  "/grahan-dosh",
  "/nadi-dosh",
  "/kemdrum-dosh",
  "/diwali-2027",
  "/holi-2027",
  "/raksha-bandhan-2027",
  "/navratri-2027",
  "/maha-shivratri-2027",
  "/janmashtami-2027",
  "/makar-sankranti-2027",
  "/karwa-chauth-2027",
  "/",
  "/ai-astrologer",
  "/free-kundli",
  "/kundli-matching",
  "/thirumana-porutham",
  "/career",
  "/love-astrology",
  "/marriage",
  "/love-calculator",
  "/manglik-calculator",
  "/kaal-sarp-dosha",
  "/sade-sati-calculator",
  "/moon-sign-calculator",
  "/nakshatra-calculator",
  "/rising-sign-calculator",
  "/mahadasha-calculator",
  "/daily-horoscope",
  "/panchang",
  "/muhurat",
  "/talk-to-astrologer",
  "/vedic-astrologer-dubai",
  "/indian-astrologer-usa",
  "/how-it-works",
  "/ai-jyotish",
  "/ai-astrology",
  "/methodology",
  "/hi",
  "/free-astrology",
  "/mesh-rashifal",
  "/vrishabh-rashifal",
  "/mithun-rashifal",
  "/kark-rashifal",
  "/singh-rashifal",
  "/kanya-rashifal",
  "/tula-rashifal",
  "/vrishchik-rashifal",
  "/dhanu-rashifal",
  "/makar-rashifal",
  "/kumbh-rashifal",
  "/meen-rashifal",
  "/blog",
  "/pricing",
  "/india",
  "/usa",
  "/uae",
  "/love",
  "/horoscope",
  "/global",
  "/about",
  "/founders",
  "/contact-us",
  "/FireSigns",
  "/FullMoonBlog",
  "/PlanetaryTransits",
  "/privacy-policy",
  "/terms-and-conditions",
]);

/**
 * Language-mode landings (/kn /gu /bn /ar). These render the homepage and
 * translate it client-side, and HomePage already declares
 * `canonical = https://astroaura.ai/` on them, so Google consolidates them into
 * "/" on its own.
 *
 * They must be left ALONE rather than swept up as duplicates: adding noindex on
 * top of a cross-URL canonical sends two conflicting instructions, and emitting
 * a self-referencing hreflang would contradict the canonical outright. So they
 * get neither directive, the canonical is already the right answer.
 */
const CANONICALISED = new Set(["/kn", "/gu", "/bn", "/ar"]);

function normalise(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
}

export function isCanonicalised(pathname: string): boolean {
  return CANONICALISED.has(normalise(pathname));
}

export function isIndexable(pathname: string): boolean {
  const path = normalise(pathname);
  return INDEXABLE.has(path) || path.startsWith("/blog/");
}

// Captured at module load, before React mounts, so before any redirect can
// rewrite it. Reading window.location during render would already say "/".
const ENTRY_PATH =
  typeof window !== "undefined" ? window.location.pathname : "/";

// Hard-coded, never derived from window.location.origin: react-snap prerenders
// against localhost:3000, so deriving it would bake localhost URLs into the
// shipped HTML.
const SITE = "https://astroaura.ai";

/**
 * Declares the page as the single global English version of itself.
 *
 * The site had no hreflang at all, which leaves Google to guess whether an
 * India-written page is meant for a searcher in Dubai or Chicago. There is one
 * URL per topic, so the correct cluster is the page plus x-default, both
 * self-referencing, which is the part Google actually validates. Inventing
 * /en-us/ and /en-ae/ copies of identical content would create duplicates, not
 * reach; the market targeting is done with real per-market pages and content.
 */
/* These seven routes only return 200 at their trailing-slash URL; the bare
   form 301s to it (Amplify has no no-slash rewrite rule for them). Their
   canonicals, og:url and the sitemap all use the slash form, and hreflang
   must say the same URL or the signals contradict each other. Everything
   else serves 200 at the bare form and canonicalises there.

   /usa and /uae are NOT in this set although they once were: verified live
   2026-10-21, Amplify serves them the OPPOSITE way round (/usa/ 301s to
   /usa), so listing them here pointed hreflang en-US and en-AE at
   redirecting URLs, one of the audit's High findings. */
const SLASH_ONLY = new Set([
  "/career",
  "/love-astrology",
  "/marriage",
  "/thirumana-porutham",
  "/india",
  "/love",
  "/horoscope",
]);

/* The market cluster: one page per market for the same core offer. Each
   href must be the URL that page's canonical names: slash form for /india/
   (SLASH_ONLY), bare form for /usa (its 200 URL, and since the usa-full
   rebuild a real self-canonical NRI hub). en-AE stays OUT (dropped
   2026-10-23, audit technical item 3): /uae still canonicalises into
   /global, and a cluster member that is not an indexable canonical URL
   makes Google ignore the whole set. It returns with the /uae rebuild. */
const MARKET_CLUSTER: [string, string][] = [
  ["en", "/"],
  ["hi", "/hi"],
  ["en-IN", "/india/"],
  ["en-US", "/usa"],
];
const MARKET_PATHS = new Set(["/", "/hi", "/india", "/usa"]);

/* Pages written in Hindi with no English twin: the 12 rashifal pages. */
const HINDI_ONLY = new Set([
  "/mesh-rashifal",
  "/vrishabh-rashifal",
  "/mithun-rashifal",
  "/kark-rashifal",
  "/singh-rashifal",
  "/kanya-rashifal",
  "/tula-rashifal",
  "/vrishchik-rashifal",
  "/dhanu-rashifal",
  "/makar-rashifal",
  "/kumbh-rashifal",
  "/meen-rashifal",
]);

function setHreflang(path: string, on: boolean) {
  if (typeof document === "undefined") return;
  const existing = Array.from(
    document.querySelectorAll<HTMLLinkElement>("link[data-index-policy-alt]"),
  );
  existing.forEach((el) => el.remove());
  if (!on) return;
  const bare = normalise(path);
  const href = SITE + (SLASH_ONLY.has(bare) ? bare + "/" : bare);
  // The rashifal pages are Hindi-only: they have no English counterpart,
  // so they declare themselves hi + x-default and never claim to be the
  // global English version of anything.
  // The MARKET_CLUSTER pages are the same core offer, one page per market
  // (international list item 8, 2026-10-15). Google only honours hreflang
  // when every member emits the full cluster pointing at the others, so
  // each lists all of them plus x-default (the homepage, what a no-match
  // searcher gets). ar-AE joins only when a real Arabic page exists.
  // Every other page stays the single global English version of itself.
  const alts: [string, string][] = MARKET_PATHS.has(bare)
    ? MARKET_CLUSTER.map(([lang, p]): [string, string] => [lang, SITE + p]).concat([
        ["x-default", SITE + "/"],
      ])
    : HINDI_ONLY.has(bare)
      ? [["hi", href], ["x-default", href]]
      : [["en", href], ["x-default", href]];
  alts.forEach(([lang, url]) => {
    const l = document.createElement("link");
    l.setAttribute("rel", "alternate");
    l.setAttribute("hreflang", lang);
    l.setAttribute("href", url);
    l.setAttribute("data-index-policy-alt", "1");
    document.head.appendChild(l);
  });
}

function setDirective(on: boolean) {
  if (typeof document === "undefined") return;
  const existing = Array.from(
    document.querySelectorAll<HTMLMetaElement>("meta[data-index-policy]"),
  );
  if (!on) {
    existing.forEach((el) => el.remove());
    return;
  }
  if (existing.length) return;
  (["robots", "googlebot"] as const).forEach((name) => {
    const m = document.createElement("meta");
    m.setAttribute("name", name);
    m.setAttribute("content", "noindex, follow");
    m.setAttribute("data-index-policy", "bootstrap");
    document.head.appendChild(m);
  });
}

export default function IndexPolicy() {
  const { pathname } = useLocation();
  const navType = useNavigationType();

  // The URL the visit actually started on. A crawler's redirect off /chat to
  // "/" is a REPLACE, so the entry path stays binding through it, otherwise
  // this would helpfully strip the directive the bootstrap script just set.
  // A real click is a PUSH, and from then on the live path is what counts.
  const userNavigated = useRef(false);
  if (navType === "PUSH") userNavigated.current = true;

  // A language landing is handled by its canonical, so this component stays out
  // of the way on both counts.
  const handsOff =
    isCanonicalised(pathname) ||
    (!userNavigated.current && isCanonicalised(ENTRY_PATH));

  const indexable = userNavigated.current
    ? isIndexable(pathname)
    : isIndexable(ENTRY_PATH) && isIndexable(pathname);

  useEffect(() => {
    if (handsOff) {
      setDirective(false);
      setHreflang(pathname, false);
      return;
    }
    setDirective(!indexable);
    // Only indexable pages get an hreflang cluster; a noindex duplicate must not
    // advertise itself as the global English version of anything.
    setHreflang(pathname, indexable);
  }, [handsOff, indexable, pathname]);

  return null;
}
